import axios from 'axios';
import { getToken, setToken, removeToken } from '../utils/storage';
import type { ApiResponse, OrderCreateRequest, OrderUpdateRequest, OrderQuoteRequest } from '../types';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string, error?: any) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function onRefreshFailed(error: any) {
  refreshSubscribers.forEach((cb) => cb('', error));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string, error?: any) => void) {
  refreshSubscribers.push(cb);
}

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  const isLoginRoute = config.url && config.url.includes('/auth/login');
  if (token && !isLoginRoute) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // 上传 FormData 时让浏览器自动设置 Content-Type（含 boundary）
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    const data = error.response?.data;
    const isAuthError = data?.code === 401001 || data?.code === 400005 || error.response?.status === 401;
    // 刷新和登录接口不触发自动刷新，避免死锁
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');
    const isLoginRequest = originalRequest?.url?.includes('/auth/login');

    if (isAuthError && originalRequest && !originalRequest._retry && !isRefreshRequest && !isLoginRequest) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshRes: any = await apiClient.post('/auth/refresh');
          const newToken = refreshRes?.data?.token || refreshRes?.token;
          const expiresIn = refreshRes?.data?.expires_in || refreshRes?.expires_in || 2592000;
          if (newToken) {
            setToken(newToken, expiresIn * 1000);
            onRefreshed(newToken);
          }
          isRefreshing = false;
          return apiClient(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          onRefreshFailed(refreshErr);
          removeToken();
          window.location.href = '/login';
          return Promise.reject(refreshErr);
        }
      }

      return new Promise((resolve, reject) => {
        addRefreshSubscriber((token: string, error?: any) => {
          if (error) {
            reject(error);
            return;
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    if (error.response?.status === 401) {
      // 注册/登录页遇到 401 不自动跳转，避免死循环
      const publicPaths = ['/login', '/register'];
      if (!publicPaths.includes(window.location.pathname)) {
        removeToken();
        window.location.href = '/login';
      }
    }
    const rejectError: any = error;
    rejectError.status = error.response?.status;
    rejectError.code = data?.code;
    rejectError.responseData = data;
    return Promise.reject(rejectError);
  }
);

const api = {
  get: <T = any>(url: string, config?: any): Promise<ApiResponse<T>> => apiClient.get(url, config),
  post: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => apiClient.post(url, data),
  put: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => apiClient.put(url, data),
  delete: <T = any>(url: string): Promise<ApiResponse<T>> => apiClient.delete(url),
};

export default api;



export const orderApi = {
  list: (params?: any) => api.get('/orders', { params }),
  detail: (id: number) => api.get(`/orders/${id}`),
  get: (id: number) => api.get(`/orders/${id}`),
  create: (data: OrderCreateRequest) => api.post('/orders', data),
  update: (id: number, data: OrderUpdateRequest) => api.put(`/orders/${id}`, data),
  updateStatus: (id: number, status: number, remark?: string) => api.put(`/orders/${id}/status`, { status, remark }),
  draft: (data: OrderCreateRequest) => api.post('/orders/draft', data),
  quote: (data: OrderQuoteRequest) => api.post('/orders/quote', data),
  invoice: (id: number, data: any) => api.post(`/orders/${id}/invoice`, data),
  afterSales: (id: number) => api.get(`/orders/${id}/after-sales`),
  createAfterSales: (id: number, data: any) => api.post(`/orders/${id}/after-sales`, data),
  copy: (id: number) => api.post(`/orders/${id}/copy`),
  delete: (id: number) => api.delete(`/orders/${id}`),
  generateRender: (id: number) => api.post(`/orders/${id}/render`),
  aiRender: (data: { order_id: number }) => api.post('/ai-render', data),
  aiRenderDescription: (data: { order_id: number }) => api.post('/ai-render/description', data),
  confirmReceipt: (id: number) => api.post(`/orders/${id}/confirm-receipt`),
  financeApprove: (id: number, data: any) => api.put(`/orders/${id}/finance-approve`, data),
  financeReject: (id: number, remark?: string) => api.put(`/orders/${id}/finance-reject`, { remark }),
  // 订单相关子资源
  payments: (id: number) => api.get(`/orders/${id}/payments`),
  invoices: (id: number) => api.get(`/orders/${id}/invoices`),
  items: (id: number) => api.get(`/orders/${id}/items`),
  approvalRemarks: (id: number) => api.get(`/orders/${id}/approval-remarks`),
  createApprovalRemark: (id: number, data: any) => api.post(`/orders/${id}/approval-remarks`, data),
  // 上传体型照片（直接使用 apiClient 以支持 multipart config）
  uploadBodyPhoto: (data: FormData, no?: number) => apiClient.post(`/orders/upload-body-photo?no=${no || ''}`, data),
  uploadImage: (data: FormData, folder?: string) => apiClient.post(`/upload/image?folder=${folder || 'embroidery-images'}`, data),
  uploadRemarkImage: (data: FormData, remarkIndex: number, imageIndex: number, globalIndex?: number) => apiClient.post(`/upload/remark-image?remark_index=${remarkIndex}&image_index=${imageIndex}${globalIndex != null ? `&global_index=${globalIndex}` : ''}`, data),
  inventoryLogs: (id: number) => api.get(`/orders/${id}/inventory-logs`),
};

export const categoryApi = {
  list: (params?: any) => api.get('/categories', { params }),
};

export const craftApi = {
  list: (params?: any) => api.get('/crafts', { params }),
};

export const customerApi = {
  list: (params?: any) => api.get('/customers', { params }),
  detail: (id: number) => api.get(`/customers/${id}`),
  create: (data: any) => api.post('/customers', data),
  update: (id: number, data: any) => api.put(`/customers/${id}`, data),
  measurements: (id: number) => api.get(`/customers/${id}/measurements`),
  createMeasurement: (id: number, data: any) => api.post(`/customers/${id}/measurements`, data),
  updateMeasurement: (id: number, mid: number, data: any) => api.put(`/customers/${id}/measurements/${mid}`, data),
  deleteMeasurement: (id: number, mid: number) => api.delete(`/customers/${id}/measurements/${mid}`),
  remarks: (id: number) => api.get(`/customers/${id}/remarks`),
  createRemark: (id: number, data: any) => api.post(`/customers/${id}/remarks`, data),
  deleteRemark: (id: number, rid: number) => api.delete(`/customers/${id}/remarks/${rid}`),
};

export const notificationApi = {
  list: (params?: any) => api.get('/notifications', { params }),
  detail: (id: number) => api.get(`/notifications/${id}`),
  markRead: (id: number) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  unreadCount: () => api.get('/notifications/unread-count'),
};

export const storeApi = {
  dashboard: () => api.get('/dashboard'),
  bills: (month?: string) => api.get('/finance/store-bills', { params: { month } }),
  // B端门店信息
  storeDetail: (id: number) => api.get(`/stores/${id}`),
  updateStore: (id: number, data: any) => api.put(`/stores/${id}`, data),
  list: () => api.get('/stores'),
  uploadLogo: (id: number, data: any) => api.put(`/stores/${id}/logo`, data),
};

export const materialApi = {
  list: (params?: any) => api.get('/materials', { params }),
  fabrics: (params?: any) => api.get('/fabrics', { params }),
  linings: (params?: any) => api.get('/materials', { params: { type: 'lining', ...params } }),
  buttons: (params?: any) => api.get('/materials', { params: { type: 'button', ...params } }),
};

export const fabricInventoryApi = {
  list: (params?: any) => api.get('/fabric-inventory', { params }),
  get: (id: number) => api.get(`/fabric-inventory/${id}`),
};

// 品牌/样本册筛选下拉用：后端均无分页上限（品牌全量返回），样本册传大 page_size 一次性拉全（与admin端一致）
export const fabricBrandApi = {
  list: (params?: any) => api.get('/fabric-brands', { params }),
};

export const sampleBookApi = {
  list: (params?: any) => api.get('/sample-books', { params }),
};

export const frontCategoryApi = {
  list: (parentCode: string, params?: any) => api.get('/front-categories', { params: { parent_code: parentCode, ...params } }),
};

export const designApi = {
  categories: () => api.get('/design-categories'),
  items: (params?: any) => api.get('/design-items', { params }),
};

export const financeApi = {
  payments: (params?: any) => api.get('/payments', { params }),
  invoices: (params?: any) => api.get('/invoices', { params }),
};

export const paymentApi = {
  list: (params?: any) => api.get('/payments', { params }),
};

export const invoiceApi = {
  list: (params?: any) => api.get('/invoices', { params }),
  get: (id: number) => api.get(`/invoices/${id}`),
};

export const commissionConfigApi = {
  get: () => api.get('/commission-config'),
};

export const discountApprovalApi = {
  create: (data: any) => api.post('/discount-approvals', data),
};

export const readyWearApi = {
  categories: () => api.get('/ready-wear-categories'),
  products: (params?: any) => api.get('/ready-wear-products', { params }),
  productDetail: (id: number) => api.get(`/ready-wear-products/${id}`),
};

export const baseConfigApi = {
  baseOrderConfigs: () => api.get('/base-order-configs'),
  baseOrderConfigOptions: (params?: any) => api.get('/base-order-config-options', { params }),
  bodyFeatureCategories: () => api.get('/body-feature-categories'),
  bodyFeatureItems: (params?: any) => api.get('/body-feature-items', { params }),
  embroideryBatchConfig: (params?: any) => api.get('/embroidery-configs/batch', { params }),
  embroideryConfig: (params?: any) => api.get('/embroidery-configs', { params }),
  embroideryTypes: (params?: any) => api.get('/embroidery-types', { params }),
  embroideryFonts: (params?: any) => api.get('/embroidery-fonts', { params }),
  embroideryPositions: (params?: any) => api.get('/embroidery-positions', { params }),
  embroideryDesigns: (params?: any) => api.get('/embroidery-designs', { params }),
  embroideryHeights: (params?: any) => api.get('/embroidery-heights', { params }),
  embroideryColors: (params?: any) => api.get('/embroidery-colors', { params }),
  designSizeConfigs: (params?: any) => api.get('/design-size-configs', { params }),
};

export const authApi = {
  login: (username: string, password: string, timezone?: string) => api.post('/auth/login', { username, password, timezone }),
  register: (data: any) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  changePassword: (userId: number, data: any) => api.put(`/users/${userId}/password`, data),
};

